/**
 * 电商套图工作台 · 主页面
 *
 * 无路由参数：项目列表（深色 hero + 封面卡片网格）；
 * 有 :projectId 参数：三阶段工作台（配置 / 分镜 / 结果），左侧设置栏常驻。
 * 生成推进走平台 worker，前端对 generating 分镜每 3 秒轮询刷新。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {Coins, ImageIcon, Loader2, Plus, Trash2} from 'lucide-react';
import { api } from '../../services/api';
import toast from '../../components/Toast';
import { AppShell } from '../../platform/AppShell';
import { getApplicationIcon } from '../applications';
import { resolveBrowserMediaUrl } from '../../utils/storageUrl';
import { formatBeijingDateKey } from '../../utils/time';
import { ecommerceSuiteAPI } from './api';
import type {
  SuiteProjectDetail,
  SuiteProjectListItem,
  SuiteShot,
  SuiteTemplateSummary,
} from './api';
import { suiteTheme } from './theme';
import { StageBar, type SuiteStage } from './components/StageBar';
import { SetupSidebar, type SuiteProjectDraft, type SuiteGenParams } from './components/SetupSidebar';
import { AssetsPanel } from './components/AssetsPanel';
import { StoryboardGrid } from './components/StoryboardGrid';
import { ResultsGrid } from './components/ResultsGrid';

const STATUS_LABELS: Record<string, string> = {
  draft: '未规划',
  planned: '已规划',
  generating: '生成中',
  partial: '部分完成',
  completed: '已完成',
};

function ImagePreviewModal({ image, onClose }: { image: string | null; onClose: () => void }) {
  if (!image) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-6" onClick={onClose}>
      <div className="absolute inset-0 bg-zinc-950/85 backdrop-blur-sm" />
      <img
        src={resolveBrowserMediaUrl(image)}
        alt="预览"
        className="relative max-h-[88vh] max-w-full rounded-2xl border border-slate-200 object-contain shadow-2xl"
      />
    </div>
  );
}

function CreateProjectModal({
  creating,
  onClose,
  onCreate,
}: {
  creating: boolean;
  onClose: () => void;
  onCreate: (payload: { title: string; productName: string; productCategory: string; productDescription: string }) => void;
}) {
  const [title, setTitle] = useState('');
  const [productName, setProductName] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [productDescription, setProductDescription] = useState('');

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <button aria-label="关闭" className="absolute inset-0 bg-zinc-950/70 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden ${suiteTheme.card}`}>
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="text-base font-semibold text-zinc-800">新建套图项目</h3>
          <button type="button" onClick={onClose} className="text-zinc-500 transition hover:text-zinc-700" aria-label="关闭">
            ✕
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <div>
            <label className={suiteTheme.label}>项目名称 *</label>
            <input className={suiteTheme.input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="例如：夏季白T 套图" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={suiteTheme.label}>商品名称</label>
              <input className={suiteTheme.input} value={productName} onChange={(e) => setProductName(e.target.value)} placeholder="商品名" />
            </div>
            <div>
              <label className={suiteTheme.label}>品类</label>
              <select className={suiteTheme.select} value={productCategory} onChange={(e) => setProductCategory(e.target.value)}>
                <option value="">请选择</option>
                {['美妆', '服装', '食品', '数码', '家居', '珠宝配饰', '母婴', '运动户外', '其他'].map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className={suiteTheme.label}>商品描述（可留待进入工作台后用 AI 帮写）</label>
            <textarea
              className={`${suiteTheme.textarea} min-h-[96px]`}
              value={productDescription}
              onChange={(e) => setProductDescription(e.target.value)}
              placeholder="核心卖点、材质、适用场景…"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button type="button" className={suiteTheme.ghostBtn} onClick={onClose} disabled={creating}>取消</button>
          <button
            type="button"
            className={suiteTheme.primaryBtn}
            disabled={creating || !title.trim()}
            onClick={() => onCreate({ title: title.trim(), productName: productName.trim(), productCategory, productDescription: productDescription.trim() })}
          >
            {creating && <Loader2 className="h-4 w-4 animate-spin" />}
            创建并进入
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EcommerceSuitePage() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId?: string }>();

  const [projects, setProjects] = useState<SuiteProjectListItem[]>([]);
  const [project, setProject] = useState<SuiteProjectDetail | null>(null);
  const [templates, setTemplates] = useState<SuiteTemplateSummary[]>([]);
  const [models, setModels] = useState<Array<{ id: string; name: string }>>([]);
  const [stage, setStage] = useState<SuiteStage>('setup');
  const [isLoading, setIsLoading] = useState(true);

  const [draft, setDraft] = useState<SuiteProjectDraft>({
    title: '',
    productName: '',
    productCategory: '',
    productDescription: '',
    verifiedFacts: '',
    prohibitedClaims: '',
    targetPlatform: '',
    targetMarket: '',
    copyLanguage: '中文',
    defaultProtection: 'PIXEL_PROTECTED',
  });
  const [genParams, setGenParams] = useState<SuiteGenParams>({ model: '', size: '1024x1024', quality: 'medium' });
  const [savingProject, setSavingProject] = useState(false);
  const [assisting, setAssisting] = useState(false);

  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const [selectedShotIds, setSelectedShotIds] = useState<string[]>([]);
  const [planning, setPlanning] = useState(false);
  const [planStreamText, setPlanStreamText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [savingShot, setSavingShot] = useState(false);
  const [rerollingShotId, setRerollingShotId] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [creatingShot, setCreatingShot] = useState(false);
  const [reordering, setReordering] = useState(false);

  const [creditPreview, setCreditPreview] = useState<number | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const projectRef = useRef<SuiteProjectDetail | null>(null);
  projectRef.current = project;

  const applyDetail = useCallback((detail: SuiteProjectDetail) => {
    setProject(detail);
    setDraft({
      title: detail.title,
      productName: detail.productName ?? '',
      productCategory: detail.productCategory ?? '',
      productDescription: detail.productDescription ?? '',
      verifiedFacts: detail.verifiedFacts ?? '',
      prohibitedClaims: detail.prohibitedClaims ?? '',
      targetPlatform: detail.targetPlatform ?? '',
      targetMarket: detail.targetMarket ?? '',
      copyLanguage: detail.copyLanguage || '中文',
      defaultProtection: detail.defaultProtection,
    });
  }, []);

  // 初始加载：项目列表 / 详情 / 模板目录 / 模型
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const templateRes = await ecommerceSuiteAPI.getTemplates();
        if (cancelled) return;
        setTemplates(templateRes.data);

        try {
          const modelRes = await api.getModels();
          if (cancelled) return;
          setModels(modelRes.data.map((model) => ({ id: model.id, name: model.name })));
        } catch { /* 模型列表拉取失败不阻塞 */ }

        if (projectId) {
          const detail = await ecommerceSuiteAPI.getProject(projectId);
          if (cancelled) return;
          applyDetail(detail.data);
        } else {
          const listRes = await ecommerceSuiteAPI.getProjects();
          if (cancelled) return;
          setProjects(listRes.data);
        }
      } catch (error) {
        if (!cancelled) toast.error(error instanceof Error ? error.message : '加载失败');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, applyDetail]);

  // 生成中轮询：任意分镜 generating 时每 3 秒刷新详情
  useEffect(() => {
    if (!projectId || !project) return;
    if (!project.shots.some((shot) => shot.status === 'generating')) return;

    const timer = setInterval(async () => {
      try {
        const detail = await ecommerceSuiteAPI.getProject(projectId);
        if (projectRef.current?.id === projectId) {
          setProject(detail.data);
        }
      } catch { /* 轮询失败静默 */ }
    }, 3000);
    return () => clearInterval(timer);
  }, [projectId, project]);

  // 进入分镜/结果阶段时刷新积分预览
  useEffect(() => {
    if (!projectId || (stage !== 'storyboard' && stage !== 'results')) return;
    ecommerceSuiteAPI
      .getCreditPreview(projectId)
      .then((res) => setCreditPreview(res.data.credits))
      .catch(() => setCreditPreview(null));
  }, [projectId, stage, project?.status]);

  const refreshProject = useCallback(async () => {
    if (!projectId) return;
    try {
      const detail = await ecommerceSuiteAPI.getProject(projectId);
      applyDetail(detail.data);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '刷新项目失败');
    }
  }, [projectId, applyDetail]);

  const handleSaveProject = async () => {
    if (!project) return;
    if (!draft.title.trim()) {
      toast.error('项目名称不能为空');
      return;
    }
    try {
      setSavingProject(true);
      const res = await ecommerceSuiteAPI.updateProject(project.id, {
        title: draft.title.trim(),
        productName: draft.productName || null,
        productCategory: draft.productCategory || null,
        productDescription: draft.productDescription || null,
        verifiedFacts: draft.verifiedFacts || null,
        prohibitedClaims: draft.prohibitedClaims || null,
        targetPlatform: draft.targetPlatform || null,
        targetMarket: draft.targetMarket || null,
        copyLanguage: draft.copyLanguage || '中文',
        defaultProtection: draft.defaultProtection,
      });
      setProject((prev) => (prev ? { ...prev, ...res.data } : prev));
      toast.success('项目设置已保存');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '保存失败');
    } finally {
      setSavingProject(false);
    }
  };

  const handleAssistDescription = async () => {
    if (!project) return;
    try {
      setAssisting(true);
      const res = await ecommerceSuiteAPI.assistDescription(project.id, {
        title: draft.title || undefined,
        productName: draft.productName || undefined,
        productCategory: draft.productCategory || undefined,
        rawText: draft.productDescription || draft.productName || draft.title,
      });
      setDraft((prev) => ({ ...prev, productDescription: res.data.description }));
      toast.success('AI 文案已生成，记得保存项目设置');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'AI 帮写失败');
    } finally {
      setAssisting(false);
    }
  };

  const handleCreateProject = async (payload: { title: string; productName: string; productCategory: string; productDescription: string }) => {
    try {
      setCreating(true);
      const res = await ecommerceSuiteAPI.createProject(payload);
      setCreateOpen(false);
      navigate(`/apps/ecommerce-suite/${res.data.id}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '创建项目失败');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteProject = async (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!window.confirm('确定删除该项目？项目下的分镜与生成记录将一并删除。')) return;
    try {
      await ecommerceSuiteAPI.deleteProject(id);
      setProjects((prev) => prev.filter((item) => item.id !== id));
      toast.success('项目已删除');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '删除失败');
    }
  };

  /** 素材自动保存：成功返回最新详情（供面板回写 canonical 列表），失败返回 null（面板回滚） */
  const handleSaveAssets = async (payload: { productImages: string[]; referenceImages: string[] }): Promise<SuiteProjectDetail | null> => {
    if (!project) return null;
    try {
      const res = await ecommerceSuiteAPI.updateAssets(project.id, payload);
      setProject((prev) => (prev
        ? {
            ...prev,
            productImageList: res.data.productImageList,
            referenceImageList: res.data.referenceImageList,
          }
        : prev));
      return res.data;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '保存素材失败');
      return null;
    }
  };

  const handlePlan = async (payload: { mode: 'ai' | 'manual'; requestedTemplateIds: string[]; targetImageCount: number; userInstruction: string }) => {
    if (!project) return;
    setPlanning(true);
    setPlanStreamText('');
    setSelectedShotIds([]);
    try {
      await ecommerceSuiteAPI.planStream(project.id, payload, {
        onContent: (text) => setPlanStreamText((prev) => prev + text),
        onResult: (result) => {
          setProject((prev) =>
            prev ? { ...prev, shots: result.shots, styleLock: result.styleLock || prev.styleLock, status: 'planned' } : prev
          );
        },
        onError: (message) => toast.error(message),
      });
      await refreshProject();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'AI 规划失败');
    } finally {
      setPlanning(false);
    }
  };

  const handleUpdateShot = async (shotId: string, patch: { displayName?: string; prompt?: string; protectionMode?: 'PIXEL_PROTECTED' | 'CREATIVE' }) => {
    try {
      setSavingShot(true);
      const res = await ecommerceSuiteAPI.updateShot(shotId, patch);
      setProject((prev) =>
        prev ? { ...prev, shots: prev.shots.map((shot) => (shot.id === shotId ? res.data : shot)) } : prev
      );
      toast.success('分镜已更新');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '更新分镜失败');
    } finally {
      setSavingShot(false);
    }
  };

  const handleDeleteShot = async (shotId: string) => {
    try {
      await ecommerceSuiteAPI.deleteShot(shotId);
      setProject((prev) => (prev ? { ...prev, shots: prev.shots.filter((shot) => shot.id !== shotId) } : prev));
      setSelectedShotIds((prev) => prev.filter((id) => id !== shotId));
      toast.success('分镜已删除');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '删除分镜失败');
    }
  };

  const overridesActive = useMemo(
    () => ({
      model: genParams.model || undefined,
      size: genParams.size || undefined,
      quality: genParams.quality || undefined,
    }),
    [genParams]
  );

  const handleGenerateSelected = async (shotIds: string[]) => {
    if (!project || shotIds.length === 0) return;
    setGenerating(true);
    let submitted = 0;
    for (const shotId of shotIds) {
      try {
        await ecommerceSuiteAPI.generateShot(shotId, overridesActive);
        submitted += 1;
      } catch (error) {
        const message = error instanceof Error ? error.message : '生成失败';
        toast.error(message);
        if (message.includes('积分不足')) break;
      }
    }
    setGenerating(false);
    if (submitted > 0) {
      toast.success(`已提交 ${submitted} 个生成任务`);
      setStage('results');
      await refreshProject();
    }
  };

  const handleReroll = async (shotId: string) => {
    try {
      setRerollingShotId(shotId);
      await ecommerceSuiteAPI.rerollShot(shotId);
      setProject((prev) =>
        prev
          ? { ...prev, shots: prev.shots.map((shot) => (shot.id === shotId ? { ...shot, status: 'generating' as const } : shot)) }
          : prev
      );
      toast.success('重新生成任务已创建');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '重新生成失败');
    } finally {
      setRerollingShotId(null);
    }
  };

  const handleCreateShot = async (payload: { templateId?: string; displayName: string; prompt?: string; protectionMode: 'PIXEL_PROTECTED' | 'CREATIVE' }) => {
    if (!project) return;
    try {
      setCreatingShot(true);
      const res = await ecommerceSuiteAPI.createShot(project.id, payload);
      setProject((prev) => (prev ? { ...prev, shots: [...prev.shots, res.data] } : prev));
      toast.success('分镜已添加');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '添加分镜失败');
    } finally {
      setCreatingShot(false);
    }
  };

  const handleReorder = async (orderedIds: string[]) => {
    if (!project) return;
    const previous = project.shots;
    setProject((prev) => {
      if (!prev) return prev;
      const order = new Map(orderedIds.map((id, index) => [id, index]));
      return { ...prev, shots: [...prev.shots].sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0)) };
    });
    try {
      setReordering(true);
      await ecommerceSuiteAPI.reorderShots(project.id, orderedIds);
    } catch (error) {
      setProject((prev) => (prev ? { ...prev, shots: previous } : prev));
      toast.error(error instanceof Error ? error.message : '排序保存失败');
    } finally {
      setReordering(false);
    }
  };

  const handleReview = async (shotId: string, reviewStatus: 'approved' | 'needs_redo' | 'pending') => {
    try {
      setReviewing(true);
      const res = await ecommerceSuiteAPI.reviewShot(shotId, reviewStatus);
      setProject((prev) => {
        if (!prev) return prev;
        const shots = prev.shots.map((shot) => (shot.id === shotId ? res.data : shot));
        const allApproved = shots.length > 0 && shots.every((shot) => shot.status === 'success' && shot.reviewStatus === 'approved');
        return { ...prev, shots, status: allApproved ? 'completed' : prev.status };
      });
      toast.success(reviewStatus === 'approved' ? '已标记通过' : reviewStatus === 'needs_redo' ? '已标记待重做' : '审核状态已重置');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '审核标记失败');
    } finally {
      setReviewing(false);
    }
  };

  const handleEditSubmit = async (shotId: string, payload: { instruction: string; maskImage: string }) => {
    try {
      setEditing(true);
      await ecommerceSuiteAPI.editShot(shotId, payload);
      setProject((prev) =>
        prev
          ? { ...prev, shots: prev.shots.map((shot) => (shot.id === shotId ? { ...shot, status: 'generating' as const } : shot)) }
          : prev
      );
      toast.success('局部编辑任务已创建');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '局部编辑失败');
    } finally {
      setEditing(false);
    }
  };

  const handleRegenerateAll = async () => {
    if (!project) return;
    if (!window.confirm('将对全部分镜（生成中的除外）按风格锁重新生成，审核状态会重置，并消耗相应积分。继续？')) return;
    try {
      setRegenerating(true);
      const res = await ecommerceSuiteAPI.regenerateAll(project.id);
      const failed = res.results.filter((item) => item.error);
      toast.success(res.message || '全组重生成任务已创建');
      if (failed.length > 0) {
        toast.error(failed.map((item) => item.error).join('；'));
      }
      await refreshProject();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '全组重生成失败');
    } finally {
      setRegenerating(false);
    }
  };

  const handleBatchDownload = async (shotIds: string[]) => {
    if (!project) return;
    const successShots = project.shots.filter(
      (shot: SuiteShot) => shot.status === 'success' && shot.outputImage && shotIds.includes(shot.id)
    );
    if (successShots.length === 0) {
      toast('没有可下载的生成结果');
      return;
    }

    try {
      setDownloading(true);
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      const folder = zip.folder(project.title || '电商套图');

      let downloadedCount = 0;
      for (const shot of successShots) {
        if (!shot.outputImage) continue;
        try {
          // 循环内逐次解析：mediaToken 30 分钟过期时惰性刷新可生效
          const response = await fetch(resolveBrowserMediaUrl(shot.outputImage));
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const blob = await response.blob();
          const ext = blob.type.includes('png') ? 'png' : 'jpg';
          folder?.file(`${shot.displayName || '分镜'}_${shot.id.slice(-6)}.${ext}`, blob);
          downloadedCount += 1;
        } catch (err) {
          console.error(`下载分镜 ${shot.id} 图片失败:`, err);
        }
      }

      if (downloadedCount === 0) {
        toast('下载失败，请重试');
        return;
      }

      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${project.title || '电商套图'}_${formatBeijingDateKey()}.zip`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      toast.success(`已下载 ${downloadedCount} 张图片`);
    } catch (error) {
      console.error('批量下载失败:', error);
      toast.error('批量下载失败，请重试');
    } finally {
      setDownloading(false);
    }
  };

  const generatingCount = project?.shots.filter((shot) => shot.status === 'generating').length ?? 0;
  const successCount = project?.shots.filter((shot) => shot.status === 'success').length ?? 0;

  return (
    <AppShell
      appName="电商套图工作台"
      appSubtitle="AI 规划分镜 · 模板套图 · 像素保护"
      appIcon={(() => { const Icon = getApplicationIcon('ecommerceSuite'); return <Icon className="h-5 w-5" />; })()}
      backgroundClassName="bg-[#eef3f8]"
      headerRight={
        project && (
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <div className="hidden rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-zinc-600 md:block">
              {successCount}/{project.shots.length} 已完成
            </div>
            <div className={`hidden items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold md:inline-flex ${creditPreview !== null && creditPreview > 0 ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-zinc-500'}`}>
              <Coins className="h-4 w-4" />
              预计 {creditPreview ?? '-'} 积分
            </div>
          </div>
        )
      }
    >
      <ImagePreviewModal image={previewImage} onClose={() => setPreviewImage(null)} />

      {isLoading ? (
        <div className="flex h-[60vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : !projectId || !project ? (
        // ===================== 项目列表 =====================
        <div className="mx-auto max-w-[1280px] px-4 py-10 sm:px-8">
          <div className="grid gap-10 lg:grid-cols-[380px_1fr]">
            <div className="flex flex-col justify-center">
              <p className="mb-4 text-xs font-medium tracking-widest text-blue-600">AI 规划 · 电商套图</p>
              <h1 className="text-4xl font-bold leading-snug text-zinc-800 sm:text-5xl">
                上传一张商品图，
                <br />
                生成一整套电商图。
              </h1>
              <p className="mt-5 text-sm leading-relaxed text-zinc-500">
                分镜可编辑、像素保护与事实约束
                <br />—— 由 AI 规划，由你确认。
              </p>
              <div className="mt-8">
                <button type="button" className={suiteTheme.primaryBtn} onClick={() => setCreateOpen(true)}>
                  <Plus className="h-4 w-4" />
                  新建项目
                </button>
              </div>
            </div>

            <div>
              {projects.length === 0 ? (
                <div className={`${suiteTheme.card} flex flex-col items-center gap-3 px-6 py-20 text-center`}>
                  <ImageIcon className="h-8 w-8 text-zinc-600" />
                  <p className="text-sm text-zinc-500">还没有项目，从新建第一个套图项目开始</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {projects.map((item) => (
                    <div
                      key={item.id}
                      className={`group relative cursor-pointer overflow-hidden ${suiteTheme.card} ${suiteTheme.cardHover}`}
                      onClick={() => navigate(`/apps/ecommerce-suite/${item.id}`)}
                    >
                      <div className="relative aspect-[4/3] bg-slate-100">
                        {item.coverImages.length > 0 ? (
                          <div className="flex h-full">
                            {item.coverImages.map((cover, index) => (
                              <img
                                key={index}
                                src={cover ? resolveBrowserMediaUrl(cover) : undefined}
                                alt=""
                                className={`h-full object-cover ${item.coverImages.length > 1 ? 'flex-1' : 'w-full'}`}
                              />
                            ))}
                          </div>
                        ) : (
                          <div className="flex h-full items-center justify-center">
                            <ImageIcon className="h-8 w-8 text-zinc-700" />
                          </div>
                        )}
                        <span className={suiteTheme.badgeGold}>{item.shotCount} 张套图</span>
                      </div>
                      <div className="flex items-center justify-between gap-2 px-4 py-3">
                        <div className="min-w-0">
                          <h3 className="truncate text-sm font-semibold text-zinc-800">{item.title}</h3>
                          <p className="mt-0.5 text-xs text-zinc-500">
                            {STATUS_LABELS[item.status] ?? item.status} · {item.completedCount}/{item.shotCount} 完成
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={(event) => handleDeleteProject(item.id, event)}
                          className="shrink-0 rounded-lg p-1.5 text-zinc-600 opacity-0 transition group-hover:opacity-100 hover:text-rose-600"
                          aria-label="删除项目"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        // ===================== 三阶段工作台 =====================
        <div className="mx-auto flex h-[calc(100vh-64px)] max-w-[1500px] flex-col gap-4 px-3 py-4 sm:px-6">
          <div className="flex items-center gap-4">
            <StageBar current={stage} onChange={setStage} />
            {generatingCount > 0 && (
              <span className={suiteTheme.badgeGold}>
                <Loader2 className="h-3 w-3 animate-spin" />
                {generatingCount} 张生成中
              </span>
            )}
          </div>

          <div className="flex min-h-0 flex-1 gap-4 overflow-hidden">
            <div className="hidden overflow-y-auto pb-4 lg:block">
              <SetupSidebar
                project={project}
                draft={draft}
                onDraftChange={(patch) => setDraft((prev) => ({ ...prev, ...patch }))}
                onSave={handleSaveProject}
                saving={savingProject}
                onAssistDescription={handleAssistDescription}
                assisting={assisting}
                models={models}
                genParams={genParams}
                onGenParamsChange={(patch) => setGenParams((prev) => ({ ...prev, ...patch }))}
              />
            </div>

            <main className="min-h-0 flex-1 overflow-y-auto pb-8">
              {stage === 'setup' && (
                <AssetsPanel
                  project={project}
                  onSave={handleSaveAssets}
                  onPreview={setPreviewImage}
                />
              )}
              {stage === 'storyboard' && (
                <StoryboardGrid
                  shots={project.shots}
                  templates={templates}
                  styleLock={project.styleLock}
                  selected={selectedShotIds}
                  onToggleSelect={(shotId) =>
                    setSelectedShotIds((prev) =>
                      prev.includes(shotId) ? prev.filter((id) => id !== shotId) : [...prev, shotId]
                    )
                  }
                  onSelectAll={() => {
                    const selectable = project.shots
                      .filter((shot) => shot.status !== 'generating' && shot.status !== 'success')
                      .map((shot) => shot.id);
                    const allSelected = selectable.length > 0 && selectable.every((id) => selectedShotIds.includes(id));
                    setSelectedShotIds(allSelected ? [] : selectable);
                  }}
                  onPlan={handlePlan}
                  planning={planning}
                  planStreamText={planStreamText}
                  onGenerateSelected={handleGenerateSelected}
                  generating={generating}
                  onUpdateShot={handleUpdateShot}
                  onDeleteShot={handleDeleteShot}
                  savingShot={savingShot}
                  onCreateShot={handleCreateShot}
                  creatingShot={creatingShot}
                  onReorder={handleReorder}
                  reordering={reordering}
                />
              )}
              {stage === 'results' && (
                <ResultsGrid
                  shots={project.shots}
                  downloading={downloading}
                  onReroll={handleReroll}
                  rerollingShotId={rerollingShotId}
                  onPreview={setPreviewImage}
                  onBatchDownload={handleBatchDownload}
                  onReview={handleReview}
                  reviewing={reviewing}
                  onEditSubmit={handleEditSubmit}
                  editing={editing}
                  onRegenerateAll={handleRegenerateAll}
                  regenerating={regenerating}
                />
              )}
            </main>
          </div>
        </div>
      )}

      {createOpen && (
        <CreateProjectModal
          creating={creating}
          onClose={() => setCreateOpen(false)}
          onCreate={handleCreateProject}
        />
      )}
    </AppShell>
  );
}
